import { useState } from 'react';
import axios from 'axios';
import { CalendarDays, Copy, Check, Download, RotateCw } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CalendarSubscribe = () => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const generate = async (rotate = false) => {
    setLoading(true);
    try {
      const authToken = localStorage.getItem('token');
      const res = await axios.post(`${API}/calendar/token`, {}, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setToken(res.data.token);
      setCopied(false);
      if (!rotate) setOpen(true);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to generate calendar link');
    } finally {
      setLoading(false);
    }
  };

  const subscribeUrl = token
    ? `${BACKEND_URL.replace(/^https?:\/\//, 'webcal://')}/api/calendar/ics?token=${token}`
    : null;
  const httpsUrl = token ? `${BACKEND_URL}/api/calendar/ics?token=${token}` : null;

  const copy = async () => {
    if (!httpsUrl) return;
    try {
      await navigator.clipboard.writeText(httpsUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_e) {
      const el = document.createElement('textarea');
      el.value = httpsUrl;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => (token ? setOpen(true) : generate())}
        disabled={loading}
        className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-2 rounded-full bg-surface-muted hover:bg-border text-text-primary transition-colors disabled:opacity-50"
        data-testid="calendar-subscribe-open"
      >
        <CalendarDays className="w-3.5 h-3.5" />
        {loading ? t('calendar.subscribe.generating', 'Generating…') : t('calendar.subscribe.cta', 'Sync to Calendar')}
      </button>
    );
  }

  return (
    <div className="bg-surface rounded-2xl p-5 border border-border max-w-md" data-testid="calendar-subscribe-panel">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="font-heading font-bold text-text-primary text-sm mb-1">
            {t('calendar.subscribe.title', 'Sync your bookings to any calendar')}
          </h3>
          <p className="text-xs text-text-secondary leading-relaxed">
            {t('calendar.subscribe.body', 'Add this URL to Google Calendar, Apple Calendar, or Outlook. Your bookings will auto-refresh every hour.')}
          </p>
        </div>
        <button
          onClick={() => setOpen(false)}
          className="text-text-secondary hover:text-text-primary text-xs"
          data-testid="calendar-subscribe-close"
          aria-label="close"
        >
          ✕
        </button>
      </div>

      <div className="bg-surface-muted rounded-xl p-3 mb-3 break-all text-xs text-text-secondary font-mono" data-testid="calendar-subscribe-url">
        {httpsUrl}
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          onClick={copy}
          className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full bg-text-primary text-white hover:bg-primary transition-colors"
          data-testid="calendar-subscribe-copy"
        >
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? t('calendar.subscribe.copied', 'Copied!') : t('calendar.subscribe.copy', 'Copy URL')}
        </button>
        <a
          href={httpsUrl}
          download="matchmycleaner.ics"
          className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-border hover:bg-surface-muted text-text-primary"
          data-testid="calendar-subscribe-download"
        >
          <Download className="w-3.5 h-3.5" />
          {t('calendar.subscribe.download', 'Download .ics')}
        </a>
        <button
          onClick={() => generate(true)}
          disabled={loading}
          className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-border hover:bg-surface-muted text-text-primary disabled:opacity-50"
          data-testid="calendar-subscribe-rotate"
          title={t('calendar.subscribe.rotateTooltip', 'Generates a new URL and invalidates the old one')}
        >
          <RotateCw className="w-3.5 h-3.5" />
          {t('calendar.subscribe.rotate', 'Rotate')}
        </button>
      </div>

      <p className="text-[10px] text-text-secondary mt-3 leading-snug">
        {t('calendar.subscribe.help', 'In Google Calendar: Settings → Add calendar → From URL. In Apple Calendar: File → New Calendar Subscription.')}
      </p>
    </div>
  );
};

export default CalendarSubscribe;
